import { CPCI_ASSESSMENT_NAMES, CPCI_ASSESSMENT_DESCRIPTIONS } from './constants';

// Route param for the COS (CPCI) catalogue preview. Preview is read-only: it
// never builds an assessment, so it needs no credits/tokens and works for any
// plan that offers COS. Real module codes are `<name>_<timestamp>`, never this.
export const COS_PREVIEW_CODE = 'cos';
export const COS_PREVIEW_PATH = `/dq_pro/product/view/${COS_PREVIEW_CODE}/preview`;

// A built assessment is a CPC Index one when its indicators are CPCI-namespaced.
export const isCpciAssessment = (a) =>
    Array.isArray(a?.indicator_codes) &&
    a.indicator_codes.some(c => String(c).toUpperCase().startsWith('CPCI'));

// Flatten the package → module → indicator tree into a flat indicator list.
// Every indicator is included (selected by default for CPCI).
export const flattenIndicators = (tree) => {
    const indicators = [];
    Object.values(tree || {}).forEach((pkgObj) => {
        const { description: _pkgDesc, ...modulesObj } = pkgObj;
        Object.values(modulesObj).forEach((modObj) => {
            const { description: _modDesc, ...indicatorsObj } = modObj;
            Object.entries(indicatorsObj).forEach(([indName, indObj]) => {
                indicators.push({
                    name: indName,
                    description: indObj.description,
                    questions: indObj.no_of_questions,
                    duration: indObj.duration,
                    indicator_code: indObj.indicator_code,
                    dqco_no: indObj.dqco_no,
                });
            });
        });
    });
    return indicators;
};

// Reduce a flat indicator list into the summary fields the CPCI build payload needs.
export const summarizeIndicators = (indicators) => ({
    indicator_codes: indicators.map(i => i.indicator_code),
    length_questions: indicators.reduce((t, i) => t + (i.questions || 0), 0),
    // Sum of each indicator's real duration, rounded to a whole minute for display
    // (e.g. "34 min", not "34.3 min") — the sum itself is the actual time it takes,
    // just presented without misleading fractional precision.
    time_to_complete_minutes: Math.round(indicators.reduce((t, i) => t + (i.duration || 0), 0)),
    more_info: indicators.map(i => ({ title: i.name, description: i.description })),
    indicators_descriptions: indicators.map(i => i.description),
});

const DEFAULT_THEME_COLOR = '#4A90E2';
const DEFAULT_BADGE_COLOR = '#1A194C';
const DEFAULT_PRIMARY_COLOR = '#FFFFFF';

/**
 * Fixed English-only presentation for every CPC Index Assessment (name/description/theme).
 * Builds the full payload ready for buildInsiderAssessmentService, given a user_group and
 * either raw indicators (flattenIndicators output) or an already-summarized object.
 */
export const buildCpciAssessmentPayload = (user_group, indicatorsOrSummary) => {
    const summary = Array.isArray(indicatorsOrSummary)
        ? summarizeIndicators(indicatorsOrSummary)
        : indicatorsOrSummary;

    const full_name = CPCI_ASSESSMENT_NAMES['en-US'];
    const description = CPCI_ASSESSMENT_DESCRIPTIONS['en-US'];

    return {
        code: `${full_name.trim().toLowerCase().replace(/\s+/g, '_')}_${Date.now()}`,
        full_name,
        description,
        user_group,
        indicator_codes: summary.indicator_codes,
        length_questions: summary.length_questions,
        time_to_complete_minutes: summary.time_to_complete_minutes,
        image_url: '',
        theme_color: DEFAULT_THEME_COLOR,
        badge_background: DEFAULT_BADGE_COLOR,
        primary_color: DEFAULT_PRIMARY_COLOR,
        more_info: summary.more_info,
        translations: {},
    };
};
